<template>
    <b-modal id="multiple-edit-discrepancy-report" title="Discrepancy Reconciliation" ref="modal" ok-title="Save"
        @ok="handleOk" @hidden="onReset" :cancel-disabled="disableConfirmButtons" :ok-disabled="disableConfirmButtons"
        :no-close-on-backdrop="true" centered size="xxl">
        <loading :active.sync="isLoading" loader="spinner" color="#20A8D8" :is-full-page="false" />

        <b-row>
            <b-col sm="2">
                <b-form-group label="Select Month and Year">
                    <b-form-datepicker name="Date" v-model="form.date" locale="en" reset-button
                        label-reset-button="Clear" :date-format-options="dateFormatOptions"
                        :date-disabled-fn="dateFromDisabled" v-validate="'required'" />
                </b-form-group>
            </b-col>
            <b-col sm="5">
                <b-form-group label="Source">
                    <v-select class="style-chooser" label="text" placeholder=" - Please select - " v-model="form.source"
                        :reduce="(source) => source.value" :options="allSourceCompanyOptions">
                    </v-select>
                </b-form-group>
            </b-col>
            <b-col sm="5">
                <b-form-group label="Destination">
                    <v-select class="style-chooser" label="text" placeholder=" - Please select - "
                        v-model="form.destination" :reduce="(destination) => destination.value"
                        :options="allDestinationCompanyOptions">
                    </v-select>
                </b-form-group>
            </b-col>
        </b-row>

        <div>
            <b-row>
                <b-col sm="6" md="4" offset-md="8" class="mt-4 mb-2 text-md-right">
                    <b-input-group prepend="Show" append="/ Page">
                        <b-form-select :options="pageOptions" v-model="perPage" />
                    </b-input-group>
                </b-col>
            </b-row>

            <b-table show-empty striped hover :items="items" :fields="fields" :current-page="currentPage"
                :per-page="perPage" :filter="filter" :sort-by.sync="sortBy" :sort-desc.sync="sortDesc"
                :sort-direction="sortDirection" responsive>
                <template v-slot:cell(dispatchNo)="row">
                    <span class="numFont">
                        {{ row.item.dispatchNo }}
                    </span>
                </template>
                <template v-slot:cell(source)="row">
                    {{ row.item["Source"] }}
                </template>
                <template v-slot:cell(destination)="row">
                    {{ row.item["Destination"] }}
                </template>
                <template v-slot:cell(discrepancies)="row">
                    <div class="numFont">
                        <b-form-input v-model.number="row.item.totalDiscrepancies" type="number"
                            :name="'TotalDiscrepancies' + row.index" size="sm"
                            v-validate="'required|discrepancyRange:1,' + row.item.OriginalDiscrepancies"
                            class="text-center" @keypress="preventInvalidKey" />
                        <span v-show="errors.has('TotalDiscrepancies' + row.index)" class="help-block">
                            Reconciliation count must be within discrepancy total
                        </span>
                    </div>
                </template>
                <template v-slot:cell(currentTotal)="row">
                    {{ row.item.currentTotal }}
                </template>
                <template v-slot:cell(adjustedTotal)="row">
                    {{ adjustedTotal(row.index) }}
                </template>
            </b-table>

            <b-row>
                <b-col sm="10" class="my-1">
                    <span class="total-display">Total: {{ items.length }}</span>
                </b-col>
                <b-col sm="2">
                    <b-pagination align="right" :total-rows="items.length" :per-page="perPage" v-model="currentPage"
                        class="my-0" />
                </b-col>
            </b-row>
        </div>

        <b-row>
            <b-col sm="6">
                <b-form-group class="mt-2" label="Remarks*" label-class="font-weight-bold pt-0" label-for="Remarks">
                    <b-form-textarea name="Remarks" type="text" maxlength="200" v-model="form.remarks" v-validate="{
                        required: true,
                        regex: remarksRegex,
                    }" :rows="3" placeholder="Remarks" />
                    <span v-show="errors.has('Remarks')" class="help-block">
                        {{ errors.first("Remarks") }}
                    </span>
                </b-form-group>
            </b-col>
        </b-row>
    </b-modal>
</template>

<script>
// API
import discrepancyReportApi from "@/api/discrepancyReportApi";

// Util
import { DateUtil } from '@/utils/dateutil';
import { DispatchUtil } from '@/utils/dispatchUtil';
import { DiscrepancyReportUtil } from "@/utils/discrepancyReportUtil";
import { DropDownItemsUtil } from '@/utils/dropDownItemsUtil';

// API
import dispatchApi from '@/api/dispatchApi';

// Others
import EventBus from '@/shared/event-bus';
import config from '@/config/env-constants';
import Loading from 'vue-loading-overlay';
import 'vue-loading-overlay/dist/vue-loading.css';
import moment from "moment";
import _ from 'lodash';

export default {
    name: 'edit-discrepancy-report',
    components: {
        Loading,
    },
    data() {
        return {
            form: {
                date: moment().format('YYYY-MM-DD'),
                source: { ...config.companyDefaultValue },
                destination: { ...config.companyDefaultValue },
                remarks: '',
            },
            items: [],
            fields: [
                {
                    key: 'dispatchNo',
                    label: 'Dispatch No.',
                    sortable: true,
                },
                {
                    key: 'Source',
                    sortable: true,
                    thClass: 'align-content-center',
                },
                {
                    key: 'Destination',
                    sortable: true,
                    thClass: 'align-content-center',
                },
                {
                    key: 'Discrepancies',
                    label: 'Total Discrepancies',
                    sortable: true,
                    tdClass: 'text-center',
                },
                {
                    key: 'currentTotal',
                    label: 'Current Total',
                    sortable: true,
                },
                {
                    key: 'adjustedTotal',
                    label: 'Adjusted Total',
                    sortable: true,
                },
            ],

            editingRowIndex: null,

            currentPage: 1,
            perPage: 10,
            totalRows: 0,
            pageOptions: [5, 10, 15, 25, 50, 100],
            sortBy: null,
            sortDesc: false,
            sortDirection: 'asc',
            filter: null,

            dateFormatOptions: { month: 'long', year: 'numeric', day: undefined },

            allCompaniesObj: {},
            allConnectedCompaniesObj: {},
            allDispatchesObj: {},
            allAssetPoolDistributionsObj: {},

            allSourceCompanyOptions: [],
            allDestinationCompanyOptions: [],

            selDiscrepancyReports: [{}],

            currUserId: this.$store.getters.loggedUser.id,

            isSuperAdmin: this.$store.getters.isSuperAdmin,
            loggedUser: this.$store.getters.loggedUser,
            loggedUserCompany: this.$store.getters.loggedUserCompany,

            // Check for loader
            isLoading: false,
        };
    },
    mounted() {
        setTimeout(async () => {
            try {
                // Show loading indicator
                this.isLoading = true;

                this.allCompaniesObj = { ...this.$store.getters.companies };
                this.allConnectedCompaniesObj = { ...this.$store.getters.connectedCompanies };

                // Create Source and Destination Company Options
                if (this.isSuperAdmin) {
                    this.allSourceCompanyOptions = DropDownItemsUtil.retrieveCompanies(this.allCompaniesObj);
                    this.allDestinationCompanyOptions = DropDownItemsUtil.retrieveCompanies(this.allCompaniesObj);
                } else {
                    this.allSourceCompanyOptions = DropDownItemsUtil.retrieveCompanies(this.allCompaniesObj);
                    this.allDestinationCompanyOptions = DropDownItemsUtil.retrieveCompanies(this.allConnectedCompaniesObj);
                }
            } catch (error) {
                this.$toaster.error('Error loading data. Please reload the page again.');
            } finally {
                // hide loading indicator
                this.isLoading = false;
            }
        }, config.timeout);
    }, watch: {
        'form.date'() {
            this.filterUpdate();
        },
        'form.source'(newVal) {
            // Destination companies includes connected
            const destinationAllCompaniesObj = { ...this.allCompaniesObj, ...this.allConnectedCompaniesObj };

            if (!this.isSuperAdmin && newVal && newVal.id !== null) {
                let companyId = newVal.id;
                let parentCompanyId = newVal.parentCompanyId;

                if (companyId !== this.loggedUserCompany.id && parentCompanyId !== this.loggedUserCompany.id) {
                    let companiesObj = _.filter(destinationAllCompaniesObj, o => {
                        return o.id === this.loggedUserCompany.id || o.parentCompanyId === this.loggedUserCompany.id;
                    });
                    this.allDestinationCompanyOptions = DropDownItemsUtil.retrieveCompanies(companiesObj);
                } else {
                    this.allDestinationCompanyOptions = DropDownItemsUtil.retrieveCompanies(destinationAllCompaniesObj);
                }
            } else {
                this.allDestinationCompanyOptions = DropDownItemsUtil.retrieveCompanies(destinationAllCompaniesObj);
            }

            // Reset selected if not on new set of dropdown
            if (this.form.destination) {
                if (!_.some(this.allDestinationCompanyOptions, { value: { id: this.form.destination.id } })) {
                    this.form.destination = { ...config.companyDefaultValue }
                }
            }

            this.filterUpdate();
        },
        'form.destination'(newVal) {
            if (!this.isSuperAdmin && newVal && newVal.id !== null) {
                let companyId = newVal.id;
                let parentCompanyId = newVal.parentCompanyId;

                if (companyId !== this.loggedUserCompany.id && parentCompanyId !== this.loggedUserCompany.id) {
                    let companiesObj = _.filter(this.allCompaniesObj, o => {
                        return o.id === this.loggedUserCompany.id || o.parentCompanyId === this.loggedUserCompany.id;
                    });
                    this.allSourceCompanyOptions = DropDownItemsUtil.retrieveCompanies(companiesObj);
                } else {
                    this.allSourceCompanyOptions = DropDownItemsUtil.retrieveCompanies(this.allCompaniesObj);
                }
            } else {
                this.allSourceCompanyOptions = DropDownItemsUtil.retrieveCompanies(this.allCompaniesObj);
            }

            // Reset selected if not on new set of dropdown
            if (this.form.source) {
                if (!_.some(this.allSourceCompanyOptions, { value: { id: this.form.source.id } })) {
                    this.form.source = { ...config.companyDefaultValue }
                }
            }

            this.filterUpdate();
        },
    },
    computed: {
        disableConfirmButtons() {
            return this.isLoading;
        },
        dispatchNo() {
            return this.selDiscrepancyReports.dispatchNo;
        },
        remarksRegex() {
            return config.remarksRegex;
        }
    },
    methods: {
        dateFromDisabled(_ymd, date) {
            return date > new Date();
        },
        async filterUpdate() {
            if (!this.form.date
                || !(this.form.source !== null && this.form.source.id)
                || !(this.form.destination !== null && this.form.destination.id)) {
                return;
            }

            await this.retrieveData();
        },
        adjustedTotal(index) {
            const item = this.items[index];

            return item
                ? Number(item.totalDiscrepancies) + Number(item.currentTotal)
                : 0;
        },
        discrepancyRule(index) {
            const item = this.items[index];

            return item ? `required|discrepancyRange:1,${item.Discrepancies}` : '';
        },
        preventInvalidKey(event) {
            const invalidChars = ['e', 'E', '+', '-', '.'];
            if (invalidChars.includes(event.key)) {
                event.preventDefault();
            }
        },
        generateDiscrepancyReport() {
            const reports = [...this.items];

            _.forEach(reports, x => {
                const assetArray = x.assets;
                assetArray.forEach(asset => {
                    if (asset.assetTypeId === x.assetTypeId) {
                        asset.discrepancies = (asset.discrepancies || 0) + x.totalDiscrepancies;
                        asset.addedDiscrepancies = x.totalDiscrepancies;
                        asset.remarks = this.form.remarks;
                    }
                });
            });

            const result = {};

            for (const report of reports) {
                const dispatchNo = report.dispatchNo;
                const targetAssetTypeId = report.assetTypeId;

                const matchedAssets = report.assets.filter(
                    asset => asset.assetTypeId === targetAssetTypeId
                );

                if (matchedAssets.length > 0) {
                    if (!result[dispatchNo]) {
                        result[dispatchNo] = { assets: [] }
                    }

                    result[dispatchNo].assets.push(...matchedAssets);
                }
            }

            _.forEach(reports, x => {
                const groupedDispatchNo = result[x.dispatchNo];

                if (groupedDispatchNo) {
                    x.assets = [...groupedDispatchNo.assets];
                }
            });

            const discrepancyReports = _.map(reports, x => DiscrepancyReportUtil.cleanupFields(x));

            return [...discrepancyReports];
        },
        getParam() {
            const discrepancyReports = this.generateDiscrepancyReport();

            return {
                currUserId: this.currUserId,
                discrepancyReportArr: [...discrepancyReports],
            };
        },
        async handleOk(evt) {
            // Prevent modal from closing
            evt.preventDefault();

            const isValid = await this.$validator.validateAll();
            if (!isValid
                || this.items.length === 0
                || !this.form.date
                || !this.form.source.id
                || !this.form.destination.id) {
                this.$toaster.warning("Please address the field/s with invalid input.");
                return;
            }

            await this.processDiscrepancyReport();
        },
        async processDiscrepancyReport() {
            // show loading indicator
            this.isLoading = true;

            try {
                const param = this.getParam();
                const { data } = await discrepancyReportApi.editDiscrepancyReport(param);

                if (data.isSuccess) {
                    this.$toaster.success(data.message);
                    this.$refs.modal.hide();
                    EventBus.$emit("onCloseEditDiscrepancyReport", data.discrepancyReportArr);
                } else {
                    this.$toaster.warning(data.message);
                }
            } catch (_error) {
                this.$toaster.error(
                    `Error updating Discrepancy Report "${this.dispatchNo}". Please try again.`
                );
            }

            // hide loading indicator
            this.isLoading = false;
        },
        onReset() {
            this.form = {
                date: moment().format('YYYY-MM-DD'),
                source: { ...config.companyDefaultValue },
                destination: { ...config.companyDefaultValue },
                remarks: '',
            };

            this.selDiscrepancyReports = {};

            this.items = [];
            this.totalRows = 0;

            // reset validation
            this.$validator.reset();
            this.errors.clear();
        },
        async retrieveData() {
            try {
                // show loading indicator
                this.isLoading = true;

                let filter = {};

                const date = new Date(this.form.date);
                const firstDay = new Date(date.getFullYear(), date.getMonth(), 1);
                const lastDay = new Date(date.getFullYear(), date.getMonth() + 1, 0);

                filter.fromTimestamp = DateUtil.startDateTimeStamp(firstDay);
                filter.toTimestamp = DateUtil.endDateTimeStamp(lastDay);

                let view = this.isSuperAdmin ? config.view.ADMIN : config.view.COMPANY;

                filter.companyId = this.loggedUserCompany.id;
                filter.status = 'Received';
                filter.sourceCompany = { id: this.form.source.id };
                filter.destinationCompany = { id: this.form.destination.id };

                const { data } = await dispatchApi.getDispatches(
                    filter,
                    view,
                    this.loggedUser.id
                );

                this.allDispatchesObj = data.dispatches;
                this.allAssetPoolDistributionsObj = data.assetPoolDistributions;

                if (_.isEmpty(this.allDispatchesObj)) {
                    this.items = [];
                    this.$toaster.warning('No Results found.');
                } else {
                    this.processDispatches(this.allDispatchesObj);
                }
            } catch (error) {
                this.$toaster.error('Error loading data. Please reload the page again.');
            } finally {
                // hide loading indicator
                this.isLoading = false;
            }
        },

        processDispatches(dispatches) {
            this.items = [];

            const assetPoolDistributions = Object.values(this.allAssetPoolDistributionsObj);

            _.forEach(dispatches, dispatch => {
                for (const asset of dispatch.assets) {
                    if (asset) {
                        // Include ONLY "Lost" discrepancy for this report
                        const discrepancies = asset.expectedQuantity - asset.actualQuantity;
                        const computedDiscrepancies = discrepancies - (asset.discrepancies || 0);
                        const status = asset.status || 'Pending';

                        if (computedDiscrepancies > 0 && status !== 'Resolved') {
                            let item = {};

                            // Parse source and destination display
                            item.id = dispatch.id;
                            item.dispatchNo = dispatch.dispatchNo;
                            item.dateDeployed = dispatch.dateDeployed;
                            item.assets = dispatch.assets;
                            item.assetTypeId = asset.assetTypeId;

                            const currentAPD = _.find(assetPoolDistributions, x =>
                                x.connectedCompanyId === dispatch.source.companyId &&
                                x.connectedStorageLocationId === dispatch.source.storageLocationId &&
                                x.assetTypeId === asset.assetTypeId &&
                                x.type === 'On-Hand'
                            );

                            item.assetPoolDistribution = currentAPD;
                            item.totalDiscrepancies = computedDiscrepancies;
                            item.currentTotal = currentAPD ? currentAPD.total : 0;
                            item.adjustedTotal = computedDiscrepancies + item.currentTotal;

                            item['Source'] = DispatchUtil.getCompanyLocationDisplay(dispatch.source);
                            item['Destination'] = DispatchUtil.getCompanyLocationDisplay(dispatch.destination);
                            item['Asset Type'] = asset.assetType;
                            item['Trucker'] = dispatch.transportation.plateNo + ' - ' + dispatch.driver.name;

                            let expectedAssets = asset.expectedQuantity;
                            let actualAssets = asset.actualQuantity;

                            // Update item properties
                            item['Dispatched'] = expectedAssets;
                            item['Received'] = actualAssets;
                            item['Discrepancies'] = computedDiscrepancies;
                            item['OriginalDiscrepancies'] = computedDiscrepancies;

                            // Parse timestamps
                            item['Date Deployed'] = this.getDisplayDateTime(dispatch.dateDeployed);
                            item['Date Received'] = this.getDisplayDateTime(dispatch.dateReceived);

                            item['Status'] = status;

                            this.items.push(item);
                        }
                    }
                }
            });

            this.items = _.sortBy(this.items, ['dateCreated']);
            this.items.reverse();
            this.totalRows = this.items.length;
        },
        getDisplayDateTime(date) {
            return DateUtil.getFormattedDateWithTime(date);
        },
    },
    beforeDestroy() {
        EventBus.$off("onUpdateDiscrepancyReport");
    },
};
</script>